/**
 * The plan editor's equipment type picker (spec 2026-08-22-02 §5).
 *
 * It is the *shared* trade-grouped picker (`components/equipment/EquipmentTypePicker`)
 * with one addition: each row is prefixed by the asset type's `plan_symbol`
 * rendered as inline SVG, so the user picks the symbol they will see on the
 * drawing. Picking also carries the type's `width_mm` / `depth_mm` back as the
 * placed element's footprint defaults.
 */
import { useTranslation } from 'react-i18next'
import { AssetTypeTile } from '../assets/AssetTypeTile'
import {
    EquipmentTypePicker as SharedEquipmentTypePicker,
    type AssetTypeLite,
} from '../equipment/EquipmentTypePicker'
import { parseViewBox } from './engine/render'
import type { PlanSymbol } from './engine/scene'

export interface PickedEquipmentType {
    code: string
    uid: string
    label: string
    widthMm?: number
    depthMm?: number
}

export const planSymbolOf = (item: { properties?: Record<string, unknown> | null }): PlanSymbol | undefined => {
    const symbol = item.properties?.plan_symbol
    if (!symbol || typeof symbol !== 'object') return undefined
    const candidate = symbol as PlanSymbol
    return candidate.svg ? candidate : undefined
}

/** A 20×20 preview of a plan symbol, scaled to fit its viewBox. */
export function PlanSymbolPreview({ symbol }: { symbol: PlanSymbol }) {
    const vb = parseViewBox(symbol.view_box)
    return (
        <svg
            width={20}
            height={20}
            viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
            className="shrink-0 text-foreground"
            aria-hidden
            // The fragment comes from our own asset-type catalog, served by the
            // API — the same trusted source `svgplan` inlines server-side.
            dangerouslySetInnerHTML={{ __html: symbol.svg }}
        />
    )
}

interface PlanEquipmentTypePickerProps {
    org: string
    open: boolean
    onOpenChange: (next: boolean) => void
    onPick: (picked: PickedEquipmentType) => void
    children: React.ReactNode
}

export function PlanEquipmentTypePicker(props: PlanEquipmentTypePickerProps) {
    const { t, i18n } = useTranslation('assets')

    const label = (item: AssetTypeLite): string => {
        if (typeof item.name === 'string') return item.name
        if (item.name && typeof item.name === 'object') {
            const m = item.name as Record<string, string>
            return m[i18n.language] || m.en || m.fr || item.code
        }
        return item.code
    }

    return (
        <SharedEquipmentTypePicker
            org={props.org}
            open={props.open}
            onOpenChange={props.onOpenChange}
            ariaLabel={t('planEditor.equipment_picker_aria')}
            renderItemPrefix={(item) => {
                const symbol = planSymbolOf(item)
                if (symbol) return <PlanSymbolPreview symbol={symbol} />
                return <AssetTypeTile type={item} size="sm" />
            }}
            onPick={(item) => {
                const symbol = planSymbolOf(item)
                props.onPick({
                    code: item.code,
                    uid: item.uid,
                    label: label(item),
                    widthMm: symbol?.width_mm,
                    depthMm: symbol?.depth_mm,
                })
            }}
        >
            {props.children}
        </SharedEquipmentTypePicker>
    )
}
